// Чистые функции экранов метрик: запрос ряда по периоду, точки для графика, разбор формы замеров.

import type { SeriesPoint } from '@/components/charts/TimeSeriesChart'
import { parseDecimal } from '@/lib/format'
import { periodRange, type Period } from '@/lib/periods'

import type { MetricType, Point, SeriesQuery } from './api'

/** Запрос ряда за период: «Всё» — без начала; агрегацию (неделя для периодов > года) выбирает сервер. */
export function seriesQuery(period: Period, today: string, trend: number | null = null): SeriesQuery {
  const range = periodRange(period, today)
  return { start: range.start, end: range.end, bucket: 'auto', trend }
}

/** Линия скользящего среднего (по дням). */
export function averagePoints(points: Point[]): SeriesPoint[] {
  return points.filter((p) => p.avg !== null).map((p) => ({ x: p.day, y: p.avg }))
}

/** Дневные точки (x — дата), чтобы линия значения и линия среднего легли на одни и те же даты. */
export function dayPoints(points: Point[]): SeriesPoint[] {
  return points.map((p) => ({ x: p.day, y: p.value }))
}

export const CATEGORY_LABELS: Record<string, string> = {
  body: 'Тело',
  measurement: 'Замеры',
  vitals: 'Показатели сердца',
  activity: 'Активность',
  recovery: 'Восстановление',
  nutrition: 'Питание',
}

const CATEGORY_ORDER = ['body', 'measurement', 'activity', 'vitals', 'recovery', 'nutrition']

/** Типы метрик, отсортированные по категориям, с подписью «Категория · Метрика». */
export function metricOptions(types: MetricType[]): { value: string; label: string }[] {
  const rank = (c: string) => {
    const i = CATEGORY_ORDER.indexOf(c)
    return i === -1 ? CATEGORY_ORDER.length : i
  }
  return [...types]
    .sort((a, b) => rank(a.category) - rank(b.category) || a.label_ru.localeCompare(b.label_ru, 'ru'))
    .map((t) => ({
      value: t.key,
      label: `${CATEGORY_LABELS[t.category] ?? t.category} · ${t.label_ru}${t.unit ? `, ${t.unit}` : ''}`,
    }))
}

/** Метрики формы замеров: обхваты (в том числе свои) и % жира. */
export function measurementTypes(types: MetricType[]): MetricType[] {
  const builtinOrder = ['neck_cm', 'chest_cm', 'waist_cm', 'hips_cm', 'biceps_cm', 'thigh_cm', 'calf_cm']
  const position = (t: MetricType) => {
    const i = builtinOrder.indexOf(t.key)
    return i === -1 ? builtinOrder.length : i
  }
  const girths = types
    .filter((t) => t.category === 'measurement')
    .sort((a, b) => Number(a.is_custom) - Number(b.is_custom) || position(a) - position(b))
  const fat = types.filter((t) => t.key === 'body_fat_pct')
  return [...girths, ...fat]
}

export type MeasurementPayload = {
  values: Record<string, number>
  errors: Record<string, string>
}

/** Форма замеров: пустые поля пропускаются, нечисловые — ошибка у поля. */
export function measurementPayload(raw: Record<string, string>): MeasurementPayload {
  const values: Record<string, number> = {}
  const errors: Record<string, string> = {}
  for (const [key, text] of Object.entries(raw)) {
    if (text.trim() === '') continue
    const value = parseDecimal(text)
    if (value === null) errors[key] = 'Нужно число'
    else values[key] = value
  }
  return { values, errors }
}

/** Значение input type="datetime-local" для момента времени (местное время). */
export function toLocalInput(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`
}

/** 'YYYY-MM-DDTHH:MM' из datetime-local → ISO-время с поясом устройства; пусто — null. */
export function fromLocalInput(value: string): string | null {
  if (!value) return null
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? null : date.toISOString()
}
